import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Button,
  Flex,
  Text,
  SimpleGrid,
} from '@chakra-ui/react'
import { Image as ChakraImage } from '@/components/HOC/Image'

import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import {
  LootboxLimited,
  LootboxLimitedDeprecated,
  LootboxMetadata,
} from '@/components/templates/Lootbox/templates/transformLootboxRewards'
import { useSession } from '@/context/SessionProvider'
import { NumberOfSoldLootboxes } from '@/components/templates/Lootbox/atoms/NumberOfSoldLootboxes'
import { useApolloClient } from '@apollo/client'
import { useQueryClient } from '@tanstack/react-query'

enum BUYING_STEPS {
  PURCHASING,
  CONFIRMATION,
}

export default ({ onClose, id }: { onClose: () => void; id: string }) => {
  const [step, setStep] = useState(BUYING_STEPS.PURCHASING)
  const [amount, setAmount] = useState(1)

  const handleNextStep = () => {
    setStep(BUYING_STEPS.CONFIRMATION)
  }

  const { getLootboxCollection } = useLootboxContext()

  const lootbox = getLootboxCollection(id) as LootboxLimited & LootboxMetadata

  return (
    <Modal size={'md'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {step === BUYING_STEPS.PURCHASING ? 'Buy ' + lootbox.title : 'Successful Purchase'}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} size="sm" />
        <ModalBody pb={5}>
          {step === BUYING_STEPS.PURCHASING && (
            <LootboxPurchase amount={amount} setAmount={setAmount} lootbox={lootbox} handleNextStep={handleNextStep} />
          )}
          {step === BUYING_STEPS.CONFIRMATION && (
            <LootboxConfirmation amount={amount} lootbox={lootbox} onClose={onClose} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const LootboxConfirmation = ({
  lootbox,
  amount,
  onClose,
}: {
  lootbox: LootboxLimitedDeprecated
  amount: number
  onClose: () => void
}) => {
  const router = useRouter()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryApolloClient = useApolloClient()

  const { getLootboxImagePrefix } = useLootboxContext()

  useEffect(() => {
    return () => {}
  }, [])

  const { account } = useSession()
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({
        queryKey: ['LOOTBOX_DENOM_BALANCE', [lootbox.open.price.denom, account?.injectiveAddress]],
      })
      queryApolloClient.refetchQueries({
        include: ['GetLootboxSales'],
      })
    }
  }, [])

  return (
    <>
      <Flex
        pos={'relative'}
        rounded={'2xl'}
        py={8}
        mb={6}
        bg={'#FFFFFF04'}
        flexDir={'column'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Box
          fontWeight={700}
          px={2}
          pos={'absolute'}
          top={4}
          right={4}
          bg={'white'}
          color={'black'}
          rounded={'xl'}
          fontSize={'sm'}
        >
          {amount} pcs
        </Box>
        <Flex
          boxShadow={`0px -12px 140px -24px ${lootbox.image.shadowColor} inset, 0px -40px 148px -42px ${lootbox.image.shadowColor} inset;`}
          rounded={'2xl'}
          justifyContent={'center'}
          alignItems={'center'}
          h={'200px'}
          mb={4}
          p={8}
        >
          <ChakraImage
            src={getLootboxImagePrefix(lootbox.contract) + (lootbox.image.src || 'lootbox.png')}
            height={155}
            width={163}
            alt=""
          />
        </Flex>
        <Text fontWeight={700}>{t('lootbox.you-bought', { title: lootbox.title })}</Text>
      </Flex>
      <SimpleGrid columns={1} gap={4}>
        {/* <Button
          onClick={() => {
            router.push('/lootbox/' + lootbox.contract)
            onClose()
          }}
          rounded={'xl'}
        >
          Open
        </Button> */}
        <Button
          onClick={() => {
            router.push('/lootbox/inventory')
            onClose()
          }}
          rounded={'xl'}
          variant="secondary"
        >
          See in inventory
        </Button>
      </SimpleGrid>
    </>
  )
}

import FlameIcon from '@/public/assets/icons/flame.svg'
import { useLootboxContext } from '@/context/LootboxProvider'

const LootboxPurchase = ({
  lootbox,
}: {
  lootbox: LootboxLimited & LootboxMetadata
  handleNextStep: () => void
  setAmount: Dispatch<SetStateAction<number>>
  amount: number
}) => {
  const { getLootboxImagePrefix } = useLootboxContext()

  return (
    <>
      <Box bg={'ninja.600'}>
        <Flex
          justifyContent={'center'}
          rounded={'2xl'}
          alignItems={'center'}
          h={'350px'}
          mb={4}
          boxShadow={`0px -12px 140px -24px ${lootbox.image.shadowColor} inset, 0px -40px 148px -42px ${lootbox.image.shadowColor} inset;`}
        >
          <ChakraImage
            src={getLootboxImagePrefix(lootbox.contract) + (lootbox.image.src || 'lootbox.png')}
            height={155}
            width={163}
            w={200}
            h={'auto'}
            alt=""
          />
        </Flex>
      </Box>
      <NumberOfSoldLootboxes id={lootbox.limited.contracts.sale} limit={lootbox.limited.quantity} version="modal" />
      {/* <PricePanel lootbox={lootbox} amount={amount} setAmount={setAmount} handleNextStep={handleNextStep} /> */}
      <Flex
        px={3}
        py={2}
        rounded={'xl'}
        bg={'#FFFFFF0A'}
        justifyContent={'space-between'}
        fontSize={'xs'}
        fontWeight={700}
      >
        <Flex gap={1} color={'ninja.100'}>
          <FlameIcon />
          The time to open the box is
        </Flex>
        <Text>3 months</Text>
      </Flex>
    </>
  )
}

// import { Amount } from '@/components/atoms/Amount/Amount'
// import { useAccountBalance } from '@/api/query'
// import { BigNumberInWei } from '@injectivelabs/utils'
// import { LootboxSalesSimpleContractExecuteMsgBuilder } from '@/api/lootbox/LootboxSalesSimpleContract.msg-builder'
// import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
// import { formatAmount } from '@/helpers/formatAmount'

// const PricePanel = ({
//   handleNextStep,
//   amount,
//   setAmount,
//   lootbox,
// }: {
//   handleNextStep: () => void
//   setAmount: Dispatch<SetStateAction<number>>
//   amount: number
//   lootbox: LootboxLimited & LootboxMetadata
// }) => {
//   return
// const { sendTransaction, account } = useSession()
// const { data: balance, isLoading: isLoadingBalance } = useAccountBalance()
// const [isLoading, setLoading] = useState(false)

// const isUserAllowedToBuy = () => {
//   return balance?.balance
//     ? new BigNumberInWei(lootbox.limited.sale.price.amount).multipliedBy(amount).lte(balance?.balance)
//     : false
// }

// const handleDecrease = () => {
//   if (amount === 1) {
//     return
//   }

//   setAmount((prev) => prev - 1)
// }

// const handleIncrease = () => {
//   setAmount((prev) => prev + 1)
// }

// const handleBuy = async () => {
//   setLoading(true)
//   try {
//     await sendTransaction(
//       MsgExecuteContractCompat.fromJSON({
//         sender: account?.injectiveAddress!,
//         contractAddress: lootbox.limited.contracts.sale,
//         funds: [
//           {
//             denom: lootbox.limited.sale.price.denom,
//             amount: new BigNumberInWei(lootbox.limited.sale.price.amount).multipliedBy(amount).toString(),
//           },
//         ],
//         msg: LootboxSalesSimpleContractExecuteMsgBuilder.buy({
//           quantity: amount.toString() as any,
//         }),
//       })
//     )

//     handleNextStep()
//   } catch {}
//   setLoading(false)
// }

// return (
//   <Box>
//     <Flex my={4} justifyContent={'space-between'} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
//       <Flex mb={{ base: 4, sm: 0 }} flexBasis={{ base: 'full', sm: '110px' }} alignItems={'center'}>
//         <Amount
//           iconSize={'20px'}
//           currencyStyles={{
//             color: 'white',
//           }}
//           denom={INJ_DENOM}
//           value={formatAmount(lootbox.limited.sale.price.amount, { precision: 2 })}
//         />
//       </Flex>
//       <Flex
//         w={'130px'}
//         justifyContent={'space-between'}
//         userSelect={'none'}
//         alignItems={'center'}
//         bg={'#FFFFFF14'}
//         rounded={'xl'}
//         fontWeight={700}
//         fontSize={18}
//       >
//         <Box
//           opacity={isLoading ? 0.4 : 1}
//           cursor={isLoading ? 'not-allowed' : 'pointer'}
//           py={3}
//           px={4}
//           onClick={() => handleDecrease()}
//         >
//           -
//         </Box>
//         <Box px={2}>{amount}</Box>
//         <Box
//           opacity={isLoading ? 0.4 : 1}
//           userSelect={'none'}
//           py={3}
//           px={4}
//           onClick={() => handleIncrease()}
//           cursor={isLoading ? 'not-allowed' : 'pointer'}
//         >
//           +
//         </Box>
//       </Flex>
//       <Button
//         isDisabled={!isUserAllowedToBuy()}
//         isLoading={isLoading || isLoadingBalance}
//         px={14}
//         onClick={handleBuy}
//         h={'52px'}
//         rounded={'xl'}
//       >
//         Buy
//       </Button>
//     </Flex>
//     <Flex
//       justifyContent={'space-between'}
//       flexWrap={{ base: 'wrap', md: 'nowrap' }}
//       alignItems={'center'}
//       py={3}
//       px={4}
//       rounded={'xl'}
//       bg={'#FFFFFF0A'}
//       mb={2}
//     >
//       <Flex gap={2}>
//         <Text fontSize={'sm'} color={'whiteAlpha.400'} fontWeight={700}>
//           Your Balance:{' '}
//         </Text>
//         <Amount
//           denom={INJ_DENOM}
//           currencyStyles={{
//             color: 'white',
//           }}
//           value={balance?.formatBalance || '0.00'}
//         />
//       </Flex>
//       {!isUserAllowedToBuy() && (
//         <Box flexBasis={{ base: '100%', md: 'auto' }} color={'#FF7D7D'} fontSize={'sm'} fontWeight={500}>
//           <Text>Insufficient funds</Text>{' '}
//         </Box>
//       )}
//     </Flex>
//   </Box>
// }
